import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import {
  InteractionRequiredAuthError,
  BrowserAuthError,
  AuthenticationResult,
} from "@azure/msal-browser";
import { apiService, AppRole } from "../services/apiService";
import { loginRequest } from "../config/authConfig";
import {
  ensureActiveMsalAccount,
  getPreferredMsalAccount,
} from "../utils/msalAccountUtils";

interface AuthContextType {
  isApiReady: boolean;
  isLoading: boolean;
  isStartingInteraction: boolean;
  error: string | null;
  hasAttemptedAuth: boolean;
  appRoles: AppRole[];
  appRolesLoaded: boolean;
  refreshAppRoles: () => Promise<void>;
  retryAuth: () => Promise<boolean>;
  beginInteractiveLogin: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isApiReady, setIsApiReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isStartingInteraction, setIsStartingInteraction] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasAttemptedAuth, setHasAttemptedAuth] = useState(false);
  const [appRoles, setAppRoles] = useState<AppRole[]>([]);
  const [appRolesLoaded, setAppRolesLoaded] = useState(false);

  const hasInitiatedInitialLoginRef = useRef(false);
  const interactionLockRef = useRef(false);
  const refreshInFlightRef = useRef<{
    promise: Promise<boolean>;
    forceRefresh: boolean;
  } | null>(null);
  const interactiveLoginRequiredRef = useRef(false);

  // MSAL hooks
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const clearAppRoles = useCallback((): void => {
    setAppRoles([]);
    setAppRolesLoaded(false);
  }, []);

  const refreshAppRoles = useCallback(async (): Promise<void> => {
    const res = await apiService.getMyRoles();
    if (res.success) {
      setAppRoles(res.data || []);
    } else {
      console.error("Failed to load app roles:", res.message);
      setAppRoles([]);
    }
    setAppRolesLoaded(true);
  }, []);

  const isInteractionRequired = (e: unknown): boolean => {
    if (e instanceof InteractionRequiredAuthError) {
      return true;
    }

    const maybeError = e as any;
    const errorCode: string | undefined = maybeError?.errorCode;
    const errorMessage: string | undefined =
      maybeError?.errorMessage || maybeError?.message;
    return (
      errorCode === "interaction_required" ||
      errorCode === "login_required" ||
      errorCode === "consent_required" ||
      Boolean(errorMessage && errorMessage.includes("interaction_required"))
    );
  };

  const isInteractionInProgressError = (e: unknown): boolean => {
    if (e instanceof BrowserAuthError) {
      return e.errorCode === "interaction_in_progress";
    }
    const maybeError = e as any;
    return maybeError?.errorCode === "interaction_in_progress";
  };

  const isPopupFailure = (e: unknown): boolean => {
    const maybeError = e as any;
    const errorCode: string | undefined = maybeError?.errorCode;
    const errorMessage: string | undefined =
      maybeError?.errorMessage || maybeError?.message;

    return (
      errorCode === "popup_window_error" ||
      errorCode === "popup_window_open_error" ||
      Boolean(errorMessage && errorMessage.toLowerCase().includes("popup"))
    );
  };

  const markSessionExpired = useCallback((): void => {
    interactiveLoginRequiredRef.current = true;
    apiService.setIdToken(null);
    setIsApiReady(false);
    clearAppRoles();
    setHasAttemptedAuth(true);
    setError("Session expired. Please log in again.");
  }, [clearAppRoles]);

  const applyAuthResultToApi = useCallback(
    (result: AuthenticationResult): void => {
      interactiveLoginRequiredRef.current = false;
      if (result.account) {
        instance.setActiveAccount(result.account);
      }
      apiService.setIdToken(result);
      setIsApiReady(true);
      setError(null);
    },
    [instance],
  );

  const beginInteractiveLogin = useCallback(async (): Promise<void> => {
    // Interactive auth must be user-triggered or a single initial bootstrap redirect.
    if (inProgress !== "none") {
      return;
    }
    if (interactionLockRef.current) {
      return;
    }

    interactionLockRef.current = true;
    setIsStartingInteraction(true);
    setError(null);

    try {
      const result = await instance.loginPopup(loginRequest);
      applyAuthResultToApi(result);
      setHasAttemptedAuth(true);
    } catch (e) {
      // If an interaction is already underway, don't turn this into a loop.
      if (isInteractionInProgressError(e)) {
        return;
      }

      if (isPopupFailure(e) && inProgress === "none") {
        try {
          await instance.loginRedirect(loginRequest);
          return;
        } catch (redirectError) {
          console.error("Redirect fallback login failed:", redirectError);
          setError(
            redirectError instanceof Error
              ? redirectError.message
              : "Redirect fallback login failed",
          );
          interactiveLoginRequiredRef.current = true;
          setIsApiReady(false);
          clearAppRoles();
          setHasAttemptedAuth(true);
          return;
        }
      }

      console.error("Interactive login failed:", e);
      setError(e instanceof Error ? e.message : "Interactive login failed");
      interactiveLoginRequiredRef.current = true;
      setIsApiReady(false);
      clearAppRoles();
      setHasAttemptedAuth(true);
    } finally {
      setIsStartingInteraction(false);
      interactionLockRef.current = false;
    }
  }, [inProgress, instance, applyAuthResultToApi, clearAppRoles]);

  const acquireTokenAndSetupApi = useCallback(async (): Promise<boolean> => {
    // Wait for MSAL to finish initialization
    if (inProgress !== "none") {
      return false;
    }

    // Don't silently restore a session that already needs interactive login.
    if (interactiveLoginRequiredRef.current) {
      return false;
    }

    try {
      setIsLoading(true);
      setError(null);

      const account = ensureActiveMsalAccount(instance, accounts);

      // Only proceed if authenticated and we have accounts
      if (!isAuthenticated || !account) {
        apiService.setIdToken(null);
        setIsApiReady(false);
        clearAppRoles();
        return false;
      }

      // Acquire token silently
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account,
      });

      applyAuthResultToApi(response);
      return true;
    } catch (error) {
      // Silent token acquisition failed.
      // If interaction is required, we stop here and let UI prompt the user.
      // This prevents automatic redirect/popup loops.
      if (isInteractionRequired(error)) {
        markSessionExpired();
        return false;
      }

      console.error("Token acquisition failed:", error);
      apiService.setIdToken(null);
      setError(
        error instanceof Error ? error.message : "Token acquisition failed",
      );
      setIsApiReady(false);
      clearAppRoles();
      return false;
    } finally {
      setIsLoading(false);
      setHasAttemptedAuth(true);
    }
  }, [
    instance,
    accounts,
    isAuthenticated,
    inProgress,
    markSessionExpired,
    applyAuthResultToApi,
    clearAppRoles,
  ]);

  // Function to ensure fresh token before API calls
  const ensureFreshToken = useCallback(
    async (forceRefresh = false): Promise<boolean> => {
      if (interactiveLoginRequiredRef.current) {
        return false;
      }

      let force = forceRefresh;
      const inFlight = refreshInFlightRef.current;
      if (inFlight) {
        if (inFlight.forceRefresh || !force) {
          return inFlight.promise;
        }
        await inFlight.promise;
        if (interactiveLoginRequiredRef.current) {
          return false;
        }
        force = true;
        if (refreshInFlightRef.current) {
          return refreshInFlightRef.current.promise;
        }
      }

      const refreshPromise = (async (): Promise<boolean> => {
        try {
          // Wait for MSAL to finish initialization
          if (inProgress !== "none") {
            return false;
          }

          const account = ensureActiveMsalAccount(instance, accounts);

          // Only proceed if authenticated and we have accounts
          if (!isAuthenticated || !account) {
            apiService.setIdToken(null);
            setIsApiReady(false);
            clearAppRoles();
            return false;
          }

          try {
            const response = await instance.acquireTokenSilent({
              ...loginRequest,
              account,
              forceRefresh: force,
            });

            applyAuthResultToApi(response);
            return true;
          } catch (silentError) {
            if (isInteractionRequired(silentError)) {
              markSessionExpired();
              return false;
            }

            // If normal silent attempt fails for non-interaction reasons,
            // retry once with forceRefresh to bypass potentially stale cache state.
            if (!force) {
              const refreshedResponse = await instance.acquireTokenSilent({
                ...loginRequest,
                account,
                forceRefresh: true,
              });

              applyAuthResultToApi(refreshedResponse);
              return true;
            }

            throw silentError;
          }
        } catch (error) {
          // Silent refresh failed. This is expected if the session is expired.
          // We don't log a warning here to avoid console spam.

          // If silent refresh fails, we do NOT trigger popup automatically in background
          // This prevents infinite loops and popups when tab is idle
          // We also mark API as not ready so components stop polling
          markSessionExpired();
          return false;
        } finally {
          refreshInFlightRef.current = null;
        }
      })();

      refreshInFlightRef.current = {
        promise: refreshPromise,
        forceRefresh: force,
      };
      return refreshPromise;
    },
    [
      instance,
      accounts,
      isAuthenticated,
      inProgress,
      applyAuthResultToApi,
      markSessionExpired,
      clearAppRoles,
    ],
  );

  // Effect to handle token acquisition and API setup
  useEffect(() => {
    // Register the token refresh callback with the API service
    apiService.setTokenRefreshCallback(ensureFreshToken);
    apiService.setAuthFailureCallback(markSessionExpired);

    const preferredAccount = getPreferredMsalAccount(instance, accounts);

    // Centralized initial login trigger (single attempt per page load).
    // This replaces App-level loginRedirect and prevents multiple competing callers.
    if (
      inProgress === "none" &&
      !isAuthenticated &&
      !preferredAccount &&
      !hasInitiatedInitialLoginRef.current
    ) {
      hasInitiatedInitialLoginRef.current = true;
      instance.loginRedirect(loginRequest).catch((e) => {
        console.error("Login redirect failed:", e);
        setError(e instanceof Error ? e.message : "Login redirect failed");
        setHasAttemptedAuth(true);
      });
      return;
    }

    acquireTokenAndSetupApi();
  }, [
    acquireTokenAndSetupApi,
    ensureFreshToken,
    markSessionExpired,
    inProgress,
    isAuthenticated,
    accounts,
    instance,
  ]);

  // Load app RBAC roles once the API token is ready
  useEffect(() => {
    if (!isApiReady) {
      return;
    }

    let cancelled = false;
    setAppRolesLoaded(false);

    (async () => {
      const res = await apiService.getMyRoles();
      if (cancelled) {
        return;
      }
      if (res.success) {
        setAppRoles(res.data || []);
      } else {
        console.error("Failed to load app roles:", res.message);
        setAppRoles([]);
      }
      setAppRolesLoaded(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [isApiReady]);

  // Manual retry: bypass MSAL cache and try silent refresh again.
  const retryAuth = useCallback(async (): Promise<boolean> => {
    interactiveLoginRequiredRef.current = false;
    return ensureFreshToken(true);
  }, [ensureFreshToken]);

  const value = {
    isApiReady,
    isLoading,
    isStartingInteraction,
    error,
    hasAttemptedAuth,
    appRoles,
    appRolesLoaded,
    refreshAppRoles,
    retryAuth,
    beginInteractiveLogin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
};
